import React, { useState,useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { dataHandlers } from "../../utils/objectHandlers.js";
import { fetchFonts } from "../../Store/reducers/fontsSlice.js";
import WebFont from "webfontloader";
import { useInView } from "react-intersection-observer";
import { setUploadedFonts } from "../../Store/reducers/uploadFontsSlice.js";
import { embedFont } from "../../utils/fontHelper.js";
import { fabric } from "fabric";   


const FontItem = ({ font, handleDragStart, handleAddText }) => {
  const fontFamily = font.family || font.font_family;
  const source = font.source;

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });


  useEffect(() => {
    if (!inView || !fontFamily) return;

    if (source === "google") {
      WebFont.load({
        google: { families: [fontFamily] },
      });
    }

    if (source === "admin" && font.src) {
      const url = font.src;
      const ext = url.split(".").pop().toLowerCase();
      const fmt =
        ext === "woff2" ? "woff2" :
        ext === "woff" ? "woff" :
        ext === "ttf" ? "truetype" : "opentype";

      const style = document.createElement("style");
      style.innerHTML = `
        @font-face {
          font-family: '${fontFamily}';
          src: url('${url}') format('${fmt}');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
      `;
      document.head.appendChild(style);
    }
  }, [inView, fontFamily, font.src, source]);

  return (
    <div
      ref={ref}
      className="flex flex-col p-2 cursor-pointer text-[13px] text-black"
      draggable
      onDragStart={(e) => handleDragStart(e, font)}
      onClick={() => handleAddText(font)}
      style={{ fontFamily }} 
    >
      {fontFamily}
    </div>
  );
};

const Fonts = ({ searchQuery }) => {
  const dispatch = useDispatch();
  const canvas = useSelector((s) => s.fabricCanvas.fabricCanvas);
  const fonts = useSelector((s) => s.fonts.fonts);
  const fontStatus = useSelector((s) => s.fonts.status);
  const uploaded = useSelector((s) => s.uploadfonts);

  const [fontMap, setFontMap] = useState({});
  const registered = useRef(new Set());

  useEffect(() => {
    if (fontStatus === "idle") dispatch(fetchFonts());
  }, [fontStatus, dispatch]);

  useEffect(() => {
    if (fontStatus !== "succeeded") return;
    const m = {};
    fonts.forEach((f) => {
      const name = f.family || f.font_family;
      const url = f.src; 
      if (name && url) m[name] = url;
    });
    setFontMap(m);
  }, [fontStatus, fonts]);

 

  // async function embedFont(name, url) {
  //   if (registered.current.has(name)) return;
  //   try {
  //     const ext = url.split(".").pop().split("?")[0].toLowerCase();
  //     const fmt =
  //       ext === "woff2" ? "woff2" :
  //       ext === "woff"  ? "woff"  :
  //       ext === "ttf"   ? "truetype" :
  //                        "opentype";

  //    if (true) {
  //      fabric.fontPaths[name] = url;                            
  //      registered.current.add(name);
  //      return;
  //    }
 
  //   } catch (err) {
  //     console.warn("embedFont failed for", name, url, err);
  //   }
  // }

  function registerFont(name, file) {
    const ext = file.name.split(".").pop().toLowerCase();
    const fmt =
      ext === "woff2" ? "woff2" :
      ext === "woff"  ? "woff"  :
      ext === "ttf"   ? "truetype" :
                       "opentype";
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      fabric.fontPaths[name] = dataUrl;
      const style = document.createElement("style");
      style.textContent = `
        @font-face {
          font-family: "${name}";
          src: url("${dataUrl}") format("${fmt}");
        }
      `;
      document.head.appendChild(style);
      registered.current.add(name);
    };
    reader.readAsDataURL(file);
  }

  const handleFontUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const name = file.name.replace(/\.[^/.]+$/, "");
    registerFont(name, file);
    dispatch(setUploadedFonts({ name, styleClass: `font-${name.toLowerCase()}` }));
  };

  const handleAddText = async (font) => {
    if (!canvas) return;
    const name = font.family || font.font_family;
    const url = fontMap[name];
    if (url) {
      await embedFont(name, url);
    }
    const x = canvas.getWidth() / 2, y = canvas.getHeight() / 2;
    dataHandlers.font(JSON.stringify(font), canvas, { left: x, top: y });
  };

  const handleDragStart = (ev, font) =>
    ev.dataTransfer.setData("font", JSON.stringify(font));

  
  const filteredUploaded = uploaded.filter((f) => {
    const n = f.name || "";
    return n.toLowerCase().includes(searchQuery.toLowerCase());       
  });

  const filteredBuiltIn = fonts.filter((f) => {
    const n = f.family || f.font_family || "";                         
    return n.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div>
      <div className="flex justify-center items-center w-full gap-2">
        <label
          htmlFor="font-upload"
          className="w-[120px] h-[38px] mb-1 flex justify-center items-center bg-white rounded-xl shadow-[0px_1px_0px_0px_rgba(227,227,227,1)_inset,1px_0px_0px_0px_rgba(227,227,227,1)_inset,-1px_0px_0px_0px_rgba(227,227,227,1)_inset,0px_-1px_0px_0px_rgba(181,181,181,1)_inset] font-semibold text-black cursor-pointer"
        >
          Upload Fonts
        </label>
        <input
          type="file"
          id="font-upload"
          accept=".ttf,.otf,.woff,.woff2"
          className="hidden"
          onChange={handleFontUpload}
        />
      </div>

      <div className="h-[1px] w-full bg-gray-200 mt-3" />

      <div className="w-full h-[370px] overflow-y-scroll custom-scrollbar pt-1 pl-4 pr-1">
        <div className="bg-[#F6F5F5] rounded-lg p-3">
          {/* Uploaded fonts section */}
          <div>
            <div className="underline text-[14px] font-semibold text-black ml-2 mt-1">
              Uploaded By You:
            </div>
            {filteredUploaded.map((font, index) => (
              <div
                key={index}
                className={`flex flex-col p-2 cursor-pointer text-[13px] ${font.styleClass}`}
                style={{  fontFamily: font.name  }}
                draggable
                onDragStart={(event) => handleDragStart(event, font)}
                onClick={() => handleAddText(font)}
              >
                {font.name}
              </div>
            ))}
          </div>

          <div className="h-[1px] w-full bg-gray-200 my-2" />

          {/* Built‑in fonts section */}
          <div className="underline font-semibold text-black ml-2 text-[14px]">
            Built-in Fonts:
          </div>
          <div>
            {filteredBuiltIn.map((font, index) => (
              <div key={index} className="relative">
              <FontItem
                
                font={font}
                handleDragStart={handleDragStart}
                handleAddText={handleAddText}
              />
                 {font.is_premium === 1 && (
                <img
                  src="https://www.svgrepo.com/download/1195/premium-badge.svg"
                  alt="Premium"
                  className="absolute top-2 right-0 w-3 h-3"
                />
              )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fonts;
